import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import styled from '@emotion/styled';
import { getCoordinates } from './countryCoordinates';

// Blueprint-style marker icon - small circle
const blueprintIcon = L.divIcon({
    className: 'blueprint-marker',
    iconSize: [8, 8],
    iconAnchor: [4, 4], // Center the icon on the coordinates
    html: '<span class="marker-dot"></span>'
});

const MapWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 1;
    background: #FAFBFC;

    .leaflet-container {
        width: 100%;
        height: 100%;
        background: #FAFBFC;
    }

    /* Blueprint-style circle markers */
    .blueprint-marker {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .marker-dot {
        width: 8px;
        height: 8px;
        background: #1a1a2e;
        border-radius: 50%;
        display: block;
    }

    /* Blueprint-style popup */
    .leaflet-popup-content-wrapper {
        background: rgba(255, 255, 255, 0.95);
        border: 1px dashed rgba(0, 0, 0, 0.2);
        border-radius: 2px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .leaflet-popup-tip {
        background: rgba(255, 255, 255, 0.95);
        border: none;
        box-shadow: none;
    }

    .leaflet-popup-close-button {
        color: #64748B !important;
        &:hover {
            color: #1a1a2e !important;
        }
    }

    /* Hide attribution */
    .leaflet-control-attribution {
        display: none;
    }
`;

interface LeafletMapLightProps {
    activeCountries: string[];
}

const LeafletMapLight: React.FC<LeafletMapLightProps> = ({ activeCountries = [] }) => {
    const [mounted, setMounted] = useState(false);
    const [geoJsonData, setGeoJsonData] = useState<any>(null);

    useEffect(() => {
        setMounted(true);
        fetch('https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json')
            .then(res => res.json())
            .then(data => setGeoJsonData(data))
            .catch(err => console.error('Failed to load map data', err));
    }, []);

    if (!mounted) return null;

    return (
        <MapWrapper>
            <MapContainer
                center={[25, 20]}
                zoom={1.5}
                scrollWheelZoom={false}
                attributionControl={false}
                zoomControl={false}
                dragging={true}
                doubleClickZoom={false}
                style={{ height: '100%', width: '100%', background: '#FAFBFC' }}
            >
                {/* Light theme tile layer as fallback */}
                {!geoJsonData && (
                    <TileLayer
                        url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                        opacity={0.6}
                    />
                )}

                {/* Country outlines with blueprint styling */}
                {geoJsonData && (
                    <GeoJSON
                        data={geoJsonData}
                        style={{
                            fillColor: '#e5e7eb',
                            weight: 1,
                            opacity: 0.8,
                            color: '#9ca3af',
                            fillOpacity: 0.5
                        }}
                    />
                )}

                {/* Active country markers */}
                {activeCountries.map((country, index) => {
                    const coords = getCoordinates(country);
                    if (!coords) return null;

                    return (
                        <Marker
                            key={`${country}-${index}`}
                            position={coords}
                            icon={blueprintIcon}
                        >
                            <Popup>
                                <div style={{
                                    fontWeight: 600,
                                    color: '#1a1a2e',
                                    marginBottom: '4px',
                                    fontFamily: 'Space Grotesk, monospace',
                                    fontSize: '0.85rem'
                                }}>
                                    {country}
                                </div>
                                <div style={{
                                    fontSize: '0.7rem',
                                    color: '#64748B',
                                    fontFamily: 'Space Grotesk, monospace',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    Active Deployment
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </MapWrapper>
    );
};

export default LeafletMapLight;
